import React, { memo, MouseEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isGalleryCategoryLoading } from 'reactApp/modules/gallery/gallery.selectors';
import { GalleryCategoryType } from 'reactApp/modules/gallery/gallery.types';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getItemsCount } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { Button } from 'reactApp/ui/Button/Button';
import { DataList } from 'reactApp/ui/Datalist/DataList';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { noop } from 'reactApp/utils/helpers';

import { IProps } from '../../SelectFromCloudPopup.types';
import { SelectFromCloudEmpty } from '../EmptyContent/SelectFromCloudEmpty';
import styles from './SelectFromCloudContent.css';

export const SelectFromCloudContent = memo(function SelectFromCloudContent(props: IProps): ReactElement {
    const { onSuccess, onClose } = props;
    const isAnyIndexSelected = useSelector(SelectionsSelectors.isAnyIndexSelected);
    const rootRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const itemsCount = useSelector((state: RootState) => getItemsCount(state, EStorageType.gallery));
    const isLoading = useSelector((state: RootState) => isGalleryCategoryLoading(state, GalleryCategoryType.all));

    const handleSuccess = useCallback(
        (e: KeyboardEvent | MouseEvent): void => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
            onSuccess?.();
        },
        [onClose, onSuccess]
    );

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
                handleSuccess(e);
            }
        };

        const stopPropagation = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
                e.stopPropagation();
            }
        };

        window.addEventListener('keypress', handler);
        window.addEventListener('keydown', stopPropagation, { capture: true });

        return () => {
            window.removeEventListener('keypress', handler);
            window.removeEventListener('keydown', stopPropagation);
        };
    }, [handleSuccess]);

    useEffect(() => {
        setVisible(true);
    }, []);

    if (isLoading && !itemsCount) {
        return <Spinner />;
    }

    if (!itemsCount) {
        return <SelectFromCloudEmpty onClose={onClose} />;
    }

    return (
        <>
            <div className={styles.datalist} ref={rootRef}>
                {visible && (
                    <DataList
                        storage={EStorageType.gallery}
                        goTo={noop}
                        viewMode={EViewMode.thumbs}
                        isPopup
                        customScrollElement={rootRef.current}
                    />
                )}
            </div>

            <div className={styles.buttonGroup} data-qa-id="button-group">
                <Button theme="vk" primary onClick={handleSuccess} className={styles.button} disabled={!isAnyIndexSelected}>
                    Добавить в альбом
                </Button>

                <Button theme="vk" className={styles.button} onClick={onClose}>
                    Отменить
                </Button>
            </div>
        </>
    );
});
